/**
 * Shared Teams Integration Utilities
 * 
 * This module provides reusable functions for Microsoft Teams integration
 * including calling, chat, and platform detection.
 */

import * as microsoftTeams from '@microsoft/teams-js';
import { teamsAuth } from './teams-auth';
import { trackUsage } from './usage';

/**
 * Initialize Teams SDK and detect platform
 * Should be called once when the app loads
 */
export async function initializeTeams(): Promise<{ isInTeams: boolean; isMobileTeams: boolean }> {
  const { isInTeams, isMobileTeams } = await teamsAuth.initializeContext();
  return { isInTeams, isMobileTeams };
}

/**
 * Get current Teams context state
 */
export function getTeamsContext() {
  const { isInTeams, isMobileTeams, isInitialized } = teamsAuth.getContextState();
  return { isInTeams, isMobileTeams, isInitialized };
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Reason a location lookup failed. Kept distinct (rather than collapsing all
 * failures to a generic "blocked") so callers can tell a permission denial
 * apart from an unsupported host, a timeout, or an SDK error — which matters
 * both for UX and for diagnosing Teams-specific issues.
 */
export type LocationErrorReason =
  | 'unsupported' // No geolocation API reachable in this host at all
  | 'denied'      // User (or policy) denied the permission
  | 'timeout'     // Position fix not obtained in time
  | 'unavailable' // Position temporarily unavailable (no GPS/network fix)
  | 'sdk-error';  // Teams SDK / browser API threw unexpectedly

export class LocationError extends Error {
  constructor(public reason: LocationErrorReason, message?: string) {
    super(message ?? reason);
    this.name = 'LocationError';
  }
}

/**
 * Whether the Teams `geoLocation` capability is usable right now.
 *
 * The capability is @beta and, in practice, only supported on Teams **mobile**
 * clients — `isSupported()` returns false on Teams desktop and Teams web. When
 * it is false we must fall back to `navigator.geolocation`, which works inside
 * the Teams desktop/web webview just like a normal browser.
 */
function isTeamsGeoLocationSupported(): boolean {
  const { isInTeams } = teamsAuth.getContextState();
  if (!isInTeams) return false;
  try {
    return microsoftTeams.geoLocation?.isSupported?.() ?? false;
  } catch {
    // isSupported() throws if app.initialize() hasn't completed. Treat as
    // unsupported so we fall back to the browser API rather than crashing.
    return false;
  }
}

function browserGeolocationAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.geolocation &&
    typeof navigator.geolocation.getCurrentPosition === 'function'
  );
}

async function getCurrentLocationViaTeams(): Promise<Coordinates> {
  // The geoLocation capability drives Teams' own consent prompt, but only when
  // the manifest declares the geolocation device permission. getCurrentLocation()
  // rejects if permission was never requested, so explicitly request it first —
  // this is what surfaces the consent prompt the user otherwise never sees.
  let granted: boolean;
  try {
    granted =
      (await microsoftTeams.geoLocation.hasPermission()) ||
      (await microsoftTeams.geoLocation.requestPermission());
  } catch (e) {
    throw new LocationError('sdk-error', `Teams geoLocation permission check failed: ${String(e)}`);
  }
  if (!granted) {
    throw new LocationError('denied', 'Teams location permission denied');
  }
  try {
    const location = await microsoftTeams.geoLocation.getCurrentLocation();
    return { latitude: location.latitude, longitude: location.longitude };
  } catch (e) {
    throw new LocationError('sdk-error', `Teams getCurrentLocation failed: ${String(e)}`);
  }
}

async function getCurrentLocationViaBrowser(timeoutMs: number): Promise<Coordinates> {
  if (!browserGeolocationAvailable()) {
    throw new LocationError('unsupported', 'Geolocation is not available');
  }
  const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(
      resolve,
      (err) => {
        // Map the W3C GeolocationPositionError codes onto our reasons so a
        // denial isn't confused with a timeout or an unavailable fix.
        if (err.code === err.PERMISSION_DENIED) {
          reject(new LocationError('denied', err.message));
        } else if (err.code === err.TIMEOUT) {
          reject(new LocationError('timeout', err.message));
        } else {
          reject(new LocationError('unavailable', err.message));
        }
      },
      {
        enableHighAccuracy: false,
        timeout: timeoutMs,
        // Accept a cached fix up to 5 minutes old. Obtaining a *fresh* fix from
        // cold (e.g. right after a Teams "Refresh" reloads the webview) can take
        // several seconds; without a generous maximumAge the request tends to
        // time out and the app reports "location cannot be detected" even though
        // a perfectly good recent position is available.
        maximumAge: 300000,
      }
    )
  );
  return { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
}

/**
 * Get the device's current location.
 *
 * Resolution order:
 * 1. Inside Teams **and** the `geoLocation` capability is supported (mobile
 *    clients): use the Teams API, which drives Teams' own consent prompt. This
 *    requires `"devicePermissions": ["geolocation"]` in the manifest.
 * 2. Otherwise (normal browser, **or** Teams desktop/web where the capability
 *    is unsupported): use `navigator.geolocation`, which works in the Teams
 *    desktop/web webview just as it does in a standalone browser.
 *
 * Rejects with a {@link LocationError} carrying a specific
 * {@link LocationErrorReason} so callers can react appropriately.
 *
 * @param options.timeoutMs - Max time to wait for a fix (browser path only).
 */
export async function getCurrentLocation(
  options: { timeoutMs?: number } = {}
): Promise<Coordinates> {
  // 10s gives a cold GPS/network fix enough time to complete. The previous 2s
  // was fine on first load (a fresh fix was already cached from the permission
  // prompt) but reliably timed out after a Teams "Refresh" tore down the webview.
  const { timeoutMs = 10000 } = options;

  if (isTeamsGeoLocationSupported()) {
    return getCurrentLocationViaTeams();
  }

  // Browser path — also covers Teams desktop/web, whose webview exposes the
  // standard navigator.geolocation even though the Teams capability is absent.
  return getCurrentLocationViaBrowser(timeoutMs);
}

/**
 * Returns true if any geolocation API is reachable in the current host.
 *
 * True when either the Teams `geoLocation` capability is supported (Teams
 * mobile) or `navigator.geolocation` exists (any browser, including the Teams
 * desktop/web webview). Use this as a guard before calling
 * `getCurrentLocation` so a genuinely missing API is treated as "unavailable"
 * rather than "permission denied".
 */
export function isGeolocationSupported(): boolean {
  return isTeamsGeoLocationSupported() || browserGeolocationAvailable();
}

/**
 * Format phone numbers and email addresses for Teams calling
 * Phone numbers need "4:+phonenumber" format for PSTN
 * Email/UPN can be used directly
 */
export function formatTeamsTarget(value: string): string {
  // If it looks like an email/UPN (contains @), use as-is
  if (value.includes('@')) {
    return value;
  }
  
  // If it's a phone number, format for PSTN
  // Remove any spaces, dashes, parentheses
  const cleanNumber = value.replace(/[\s\-\(\)]/g, '');
  // Ensure it starts with + for international format
  const phoneNumber = cleanNumber.startsWith('+') ? cleanNumber : `+${cleanNumber}`;
  return `4:${phoneNumber}`;
}

/**
 * Check if a target is a phone number (vs email/UPN)
 */
export function isPhoneNumber(value: string): boolean {
  return !value.includes('@');
}

/**
 * Initiate a Teams call (audio or video)
 * Handles both desktop and mobile Teams, PSTN and Teams-to-Teams calls
 * 
 * @param target - Phone number or email/UPN to call
 * @param options - Call options
 * @returns Promise that resolves when call is initiated
 */
export async function initiateTeamsCall(
  target: string,
  options: {
    withVideo?: boolean;
    useTeamsCalling?: boolean;
    source?: string;
  } = {}
): Promise<void> {
  const { withVideo = false, useTeamsCalling = true, source = 'QuickDial' } = options;
  const isPhone = isPhoneNumber(target);
  const { isInTeams, isMobileTeams } = teamsAuth.getContextState();

  trackUsage('call');

  if (!useTeamsCalling) {
    // Teams calling disabled - use tel: protocol for phone numbers (audio only)
    if (!withVideo && isPhone) {
      window.location.href = `tel:${target}`;
    }
    return;
  }

  if (isInTeams) {
    // Running inside Teams - use call.startCall API or deep links
    try {
      // Mobile Teams doesn't support PSTN via call.startCall API, use deep link instead
      if (isMobileTeams && isPhone) {
        console.log('Mobile Teams detected with phone number - using deep link for PSTN');
        const deepLink = withVideo
          ? `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(target)}&withVideo=true`
          : `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(target)}`;
        microsoftTeams.executeDeepLink(deepLink);
        return;
      }

      // Check if call API is supported
      if (microsoftTeams.call && microsoftTeams.call.isSupported()) {
        const formattedTarget = formatTeamsTarget(target);
        const modalities = withVideo
          ? [microsoftTeams.call.CallModalities.Audio, microsoftTeams.call.CallModalities.Video]
          : [microsoftTeams.call.CallModalities.Audio];

        await microsoftTeams.call.startCall({
          targets: [formattedTarget],
          requestedModalities: modalities,
          source
        });
      } else {
        // Fallback to deep link if call API not supported
        console.warn('Teams call API not supported, using deep link');
        const deepLink = withVideo
          ? `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(target)}&withVideo=true`
          : `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(target)}`;
        microsoftTeams.executeDeepLink(deepLink);
      }
    } catch (error) {
      console.error('Teams call failed:', error);
      throw error;
    }
  } else {
    // Not in Teams - open Teams deep link in browser
    const url = withVideo
      ? `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(target)}&withVideo=true`
      : `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(target)}`;
    window.open(url, '_blank');
  }
}

/**
 * Normalize a user-entered URL for a QuickDial "url" button.
 *
 * Admins typically type "example.com" without a scheme; treat that as https so
 * the link opens as an absolute external URL rather than a relative navigation.
 * Returns the trimmed input unchanged when it already carries an explicit scheme
 * (http/https/mailto/tel/etc.), and returns an empty string for empty input.
 */
export function normalizeUrl(value: string): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';

  // Block dangerous URI schemes explicitly.
  if (/^(javascript|data|vbscript|file):/i.test(trimmed)) {
    return '';
  }

  // Keep full URLs with an explicit scheme:// prefix as-is.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed)) {
    return trimmed;
  }

  // Keep known non-hierarchical schemes as-is.
  if (/^(mailto|tel|sms):/i.test(trimmed)) {
    return trimmed;
  }

  // Protocol-relative ("//host") also gets https to force an absolute open.
  return `https://${trimmed.replace(/^\/+/, '')}`;
}

/**
 * Whether a value normalizes to a syntactically valid URL. Used by the editor
 * to warn on obviously broken input before it is saved.
 */
export function isValidUrl(value: string): boolean {
  const normalized = normalizeUrl(value);
  if (!normalized) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(normalized);
    return true;
  } catch {
    return false;
  }
}

/**
 * Open an external URL from a QuickDial "url" button.
 *
 * Inside Teams, `window.open` to an arbitrary site is unreliable in the tab
 * iframe, so use the Teams SDK `app.openLink` which respects Teams' own link
 * handling. Outside Teams, open a new browser tab with noopener for safety.
 * The input is normalized (scheme defaulted to https) before opening.
 */
export async function openExternalLink(value: string): Promise<void> {
  const url = normalizeUrl(value);
  if (!url) return;

  const { isInTeams } = teamsAuth.getContextState();
  if (isInTeams) {
    try {
      await microsoftTeams.app.openLink(url);
      return;
    } catch (error) {
      // Fall through to a browser open if the SDK rejects (e.g. capability not
      // ready) rather than leaving the click with no effect.
      console.warn('Teams app.openLink failed, falling back to window.open:', error);
    }
  }
  const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!openedWindow) {
    throw new Error('The browser blocked opening the link. Please allow popups for this site and try again.');
  }
}

/**
 * Initiate a Teams chat
 *
 * @param target - Email/UPN to chat with
 */
export function initiateTeamsChat(target: string): void {
  const { isInTeams } = teamsAuth.getContextState();

  trackUsage('sms');

  if (isInTeams) {
    // Running inside Teams - use executeDeepLink to stay in-app
    const deepLink = `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(target)}`;
    microsoftTeams.executeDeepLink(deepLink);
  } else {
    // Not in Teams - open in browser
    window.open(`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(target)}`, '_blank');
  }
}

/**
 * Start a group Teams call (audio or video)
 * 
 * @param targets - Array of phone numbers or email/UPNs to call
 * @param withVideo - Whether to start with video enabled
 */
export function initiateGroupTeamsCall(targets: string[], withVideo: boolean = false): void {
  if (targets.length === 0) return;

  trackUsage('call');

  const { isInTeams } = teamsAuth.getContextState();

  const usersParam = targets.join(',');
  const deepLink = withVideo
    ? `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(usersParam)}&withVideo=true`
    : `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(usersParam)}`;

  if (isInTeams) {
    microsoftTeams.executeDeepLink(deepLink);
  } else {
    window.open(deepLink, '_blank');
  }
}

/**
 * Start a group Teams chat
 * 
 * @param targets - Array of email/UPNs to chat with
 */
export function initiateGroupTeamsChat(targets: string[]): void {
  if (targets.length === 0) return;

  trackUsage('sms');

  const { isInTeams } = teamsAuth.getContextState();

  const usersParam = targets.join(',');
  const deepLink = `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(usersParam)}`;

  if (isInTeams) {
    microsoftTeams.executeDeepLink(deepLink);
  } else {
    window.open(deepLink, '_blank');
  }
}
